import React, { ClipboardEvent, useEffect, useRef, useState, DragEvent } from 'react';
import '@blueprintjs/table/lib/css/table.css';
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css';
import './SequenceInput.css';
import { FieldProps } from 'formik';
import { HotkeysProvider } from '@blueprintjs/core';
import { parseFasta } from './validation';
import { DocumentIcon, TrashIcon, ArrowDownTrayIcon, ArrowLeftEndOnRectangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { CellProps, Column, FocusedCellCoordinates, Region, Table2 } from '@blueprintjs/table';
import { getInitialSequenceInputValue } from './utils';
import TextAreaCell from './TextAreaCell';
import { resizeRowsByApproximateHeight } from '@blueprintjs/table/lib/esnext/resizeRows';
import ResizableDiv from './ResizableDiv';
import PasteModal, { PasteType } from './PasteModal';
import FetchIdsModal from './FetchIdsModal/FetchIdsModal';
import { ISequenceInputValue } from '../types';
import { ISetting } from '@biolibtech/biolib-js';
import { useScrollbarWidth } from '../utils';
import { UtilityButton } from '@components/atoms/UtilityButton';
import { useGetCurrentUserQuery } from '../../../../../../apiHooks/userApi';
import { findNextAvailableRowIndex } from './FetchIdsModal/utils';

interface IProps extends FieldProps {
    autoFocus: boolean;
    setting: ISetting;
    enableFetchIds?: boolean;
}

export default function SequenceInput(props: IProps) {
    const { rows, filename, file } = props.field.value as ISequenceInputValue;

    const tableRef = useRef<Table2>();
    const containerRef = useRef<HTMLDivElement>();
    const fileInputRef = useRef<HTMLInputElement>();
    const [containerWidth, setContainerWidth] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [tableRenderInvalidator, setTableRenderInvalidator] = useState<boolean>(false);
    const [enableFocusedCell, setEnableFocusedCell] = useState<boolean>(props.autoFocus);
    const [focusedCell, setFocusedCell] = useState<FocusedCellCoordinates>({ row: 0, col: 1, focusSelectionIndex: 0 });
    const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
    const [isLocked, setIsLocked] = useState<boolean>(false);
    const scrollbarWidth = useScrollbarWidth() + 5;

    const columnWidths = [150, (containerWidth ?? 500) - 150 - scrollbarWidth];
    const [rowHeights, setRowHeights] = useState<number[]>([]);
    const [pastedTextForModal, setPastedTextForModal] = useState<string | null>(null);
    const [fetchIdsModalOpen, setFetchIdsModalOpen] = useState(false);
    const { data: currentUser } = useGetCurrentUserQuery();

    const clearTable = () => {
        props.form.setFieldTouched(props.field.name, false, false);
        props.form.setFieldValue(props.field.name, getInitialSequenceInputValue(props.setting.config), true);
        props.form.setFieldError(props.field.name, undefined);
        setTableRenderInvalidator(!tableRenderInvalidator);
        setIsLocked(false);
    };

    useEffect(() => {
        const rowHeights: number[] = resizeRowsByApproximateHeight(
            Object.keys(rows).length,
            columnWidths,
            (rowIndex: number, columnIndex: number) => rows[rowIndex][columnIndex] || '-',
            {
                getApproximateCharWidth: 7.5,
                getApproximateLineHeight: 20,
                getCellHorizontalPadding: 7,
                getNumBufferLines: 0,
            },
        );
        for (let i = 0; i < rowHeights.length; i += 1) {
            rowHeights[i] += 2; // add top and bottom padding;
        }
        setRowHeights(rowHeights);
        setTableRenderInvalidator(!tableRenderInvalidator);
    }, [containerWidth, rows]);

    const columns: string[] = ['Name', 'Sequence'];

    const updateRows = (rowsToUpdate: Record<number, Record<number, string>>, forceTableReRender: boolean) => {
        const newRows = { ...rows, ...rowsToUpdate };
        const newRowsLength = Object.keys(newRows).length;
        const lastRow = newRows[newRowsLength - 1];
        if (lastRow[0] || lastRow[1]) {
            // Last row not empty so add new empty row
            newRows[newRowsLength] = { 0: '', 1: '' };
        }
        props.form.setFieldValue(props.field.name, { filename, rows: newRows }, true);
        if (forceTableReRender) {
            setTableRenderInvalidator(!tableRenderInvalidator);
        }
    };

    useEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                setContainerWidth(Math.max(containerRef.current.offsetWidth, 100));
            }
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const cellRenderer = (rowIndex: number, columnIndex: number): React.ReactElement<CellProps> => (
        <TextAreaCell
            value={rows[rowIndex]?.[columnIndex] ?? ''}
            onFocus={() => {
                if (
                    rowIndex === 0 &&
                    rowIndex === focusedCell.row &&
                    columnIndex === 1 &&
                    columnIndex === focusedCell.col
                ) {
                    // initial focus do nothing
                } else {
                    props.form.setFieldTouched(props.field.name, true, true);
                }
            }}
            setValue={(newValue) => {
                const shouldUppercase = props.setting.config?.auto_uppercase_sequence !== false;
                const processedValue = columnIndex === 1 && shouldUppercase ? newValue.toUpperCase() : newValue;
                updateRows({ [rowIndex]: { ...rows[rowIndex], [columnIndex]: processedValue } }, false);
            }}
            readOnly={isLocked}
            placeholder={
                rowIndex !== 0
                    ? undefined
                    : columnIndex === 0
                      ? `sequence_${rowIndex + 1}`
                      : '[Write sequence or paste FASTA here]'
            }
        />
    );

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
        if (isLocked) return;
        const rawText = event.clipboardData.getData('Text').trim();
        if (!focusedCell) {
            return;
        }

        let isPastingFromSpreadsheet = true;
        for (const line of rawText.split('\n')) {
            if (!line.includes('\t')) {
                isPastingFromSpreadsheet = false;
                break;
            }
        }

        if (isPastingFromSpreadsheet) {
            // Parse multiple columns from spreadsheet
            const pastedRows = rawText.split('\n');
            const rowsToUpdate: Record<number, Record<number, string>> = {};
            const shouldUppercase = props.setting.config?.auto_uppercase_sequence !== false;
            for (let i = 0; i < pastedRows.length; i += 1) {
                const pastedColumns = pastedRows[i].trim().split('\t');

                const newRowIndex = focusedCell.row + i;
                rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                if (focusedCell.col === 0) {
                    rowsToUpdate[newRowIndex][0] = (pastedColumns[0] ?? '').replace(/\s+/g, '').replace('>', '');
                    if (pastedColumns.length > 1) {
                        const sequenceValue = (pastedColumns[1] ?? '').replace(/\s+/g, '');
                        rowsToUpdate[newRowIndex][1] = shouldUppercase ? sequenceValue.toUpperCase() : sequenceValue;
                    }
                } else if (focusedCell.col === 1) {
                    const sequenceValue = (pastedColumns[0] ?? '').replace(/\s+/g, '');
                    rowsToUpdate[newRowIndex][1] = shouldUppercase ? sequenceValue.toUpperCase() : sequenceValue;
                }
            }
            event.preventDefault();
            updateRows(rowsToUpdate, true);
        } else if (rawText.startsWith('>') && rawText.includes('\n')) {
            // Parse FASTA
            event.preventDefault();
            const rowsToUpdate: Record<number, Record<number, string>> = {};
            try {
                const records = parseFasta(rawText, props.setting.config);
                for (let i = 0; i < records.length; i += 1) {
                    const newRowIndex = focusedCell.row + i;
                    const record = records[i];
                    rowsToUpdate[newRowIndex] = { 0: record.sequenceId, 1: record.sequence };
                }
                props.form.setFieldValue(props.field.name, { filename, rows: { ...rows, ...rowsToUpdate } }, true);
                setTableRenderInvalidator(!tableRenderInvalidator);
            } catch (error) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(props.field.name, `Failed to parse pasted text as FASTA. ${error.message}`);
            }
        } else if (rawText.includes('\n')) {
            // Parse multiline text
            event.preventDefault();
            event.stopPropagation();
            setPastedTextForModal(rawText);
        } else {
            event.preventDefault();
            event.stopPropagation();
            const shouldUppercase = props.setting.config?.auto_uppercase_sequence !== false;
            let value = focusedCell.col === 0 ? rawText.trim().replace('>', '') : rawText.trim();
            if (focusedCell.col === 1 && shouldUppercase) {
                value = value.toUpperCase();
            }
            const rowsToUpdate: Record<number, Record<number, string>> = {
                [focusedCell.row]: { ...rows[focusedCell.row], [focusedCell.col]: value },
            };
            updateRows(rowsToUpdate, true);
        }
    };

    const LARGE_FILE_SIZE_THRESHOLD = 10 * 1024 * 1024; // 10 MB

    const onFileSelect = async (files: FileList) => {
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];

        if (file.size > LARGE_FILE_SIZE_THRESHOLD) {
            props.form.setFieldValue(
                props.field.name,
                { filename: file.name, rows: getInitialSequenceInputValue(props.setting.config).rows, file },
                true,
            );
            setTableRenderInvalidator(!tableRenderInvalidator);
            setIsLocked(true);
            return;
        }

        const text = await file.text();

        const rowsToUpdate: Record<number, Record<number, string>> = {};
        try {
            const records = parseFasta(text, props.setting.config);
            for (let i = 0; i < records.length; i += 1) {
                const newRowIndex = i;
                const record = records[i];
                rowsToUpdate[newRowIndex] = { 0: record.sequenceId, 1: record.sequence };
            }
            props.form.setFieldValue(
                props.field.name,
                { filename: file.name, rows: { ...getInitialSequenceInputValue(props.setting.config).rows, ...rowsToUpdate } },
                true,
            );
            setTableRenderInvalidator(!tableRenderInvalidator);
            tableRef.current.scrollToRegion({ rows: [0, 0] });
            setIsLocked(true);
        } catch (error) {
            props.form.setFieldTouched(props.field.name, true, false);
            props.form.setFieldError(props.field.name, `Failed to parse file as FASTA. ${error.message}`);
        }
    };

    const deleteSelectedRegions = () => {
        const rowsToUpdate: Record<number, Record<number, string>> = {};
        for (const region of selectedRegions) {
            const {
                rows: [rowStart, rowEnd],
                cols: [colStart, colEnd],
            } = selectedRegions[0];
            for (let i = rowStart; i < rowEnd + 1; i += 1) {
                rowsToUpdate[i] = rows[i];
                for (let j = colStart; j < colEnd + 1; j += 1) {
                    rowsToUpdate[i][j] = '';
                }
            }
        }
        updateRows(rowsToUpdate, true);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        if (isLocked) {
            return;
        }

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            if (event.dataTransfer.files.length > 1) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(
                    props.field.name,
                    'Only one file allowed. Please drag and drop a single .fasta file',
                );
            } else {
                onFileSelect(event.dataTransfer.files);
            }
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }
        setIsDragging(false);
    };

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    return (
        <div className='flex flex-col md:flex-row w-full space-y-2 md:space-y-0 md:space-x-2'>
            <PasteModal
                isOpen={!!pastedTextForModal}
                columnName={focusedCell.col === 0 ? 'name' : 'sequence'}
                onClose={(pasteType) => {
                    const shouldUppercase = props.setting.config?.auto_uppercase_sequence !== false;
                    if (pasteType === PasteType.multiple) {
                        const pastedRows = pastedTextForModal.split('\n');
                        const rowsToUpdate: Record<number, Record<number, string>> = {};
                        for (let i = 0; i < pastedRows.length; i += 1) {
                            const newRowIndex = focusedCell.row + i;
                            rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                            const value = pastedRows[i].replace(/\s+/g, '');
                            rowsToUpdate[newRowIndex][focusedCell.col] = focusedCell.col === 1 && shouldUppercase ? value.toUpperCase() : value;
                        }
                        updateRows(rowsToUpdate, true);
                    } else if (pasteType === PasteType.single) {
                        const newRowIndex = focusedCell.row;
                        const rowsToUpdate: Record<number, Record<number, string>> = {};
                        rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                        const value = pastedTextForModal.replace(/\s+/g, '');
                        rowsToUpdate[newRowIndex][focusedCell.col] = focusedCell.col === 1 && shouldUppercase ? value.toUpperCase() : value;
                        updateRows(rowsToUpdate, true);
                    }
                    setPastedTextForModal(null);
                }}
            />
            <div
                className={clsx(
                    'w-full rounded-md overflow-hidden border relative',
                    isDragging ? 'border-emerald-500' : isLocked ? 'border-gray-300 bg-gray-50' : 'border-gray-300',
                )}
                onPasteCapture={handlePaste}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDragEnter={handleDragEnter}
                ref={containerRef}
                onMouseEnter={() => {
                    document.documentElement.style.overscrollBehaviorX = 'none';
                    setEnableFocusedCell(!isLocked);
                }}
                onMouseLeave={() => (document.documentElement.style.overscrollBehaviorX = '')}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (isLocked) return;

                    if (event.key === 'Backspace') {
                        if (selectedRegions.length === 1) {
                            const { rows, cols } = selectedRegions[0];
                            const rowCount = rows[1] - rows[0];
                            const colCount = cols[1] - cols[0];
                            if (rowCount > 0 || colCount > 0) {
                                deleteSelectedRegions();
                            }
                        } else if (selectedRegions.length > 1) {
                            deleteSelectedRegions();
                        }
                    } else if (event.key === 'Delete') {
                        deleteSelectedRegions();
                    }
                }}
            >
                {isLocked && filename && (
                    <div className='z-40 mr-1 absolute top-0 right-0 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-bl-md border-l border-b border-gray-300'>
                        <span className='font-medium'>File: </span>
                        <span className='italic'>{filename}</span>
                    </div>
                )}
                {file && (
                    <div className='absolute inset-0 bg-gray-50 flex items-center justify-center z-10'>
                        <div className='text-center text-gray-600'>
                            <DocumentIcon className='h-10 w-10 mx-auto mb-2' />
                            <p className='text-sm'>File too large to preview</p>
                            <p className='text-xs text-gray-500'>({(file.size / (1024 * 1024)).toFixed(1)} MB)</p>
                        </div>
                    </div>
                )}
                {isDragging && (
                    <div className='absolute inset-0 bg-emerald-50 bg-opacity-70 flex items-center justify-center z-10'>
                        <div className='text-center'>
                            <ArrowDownTrayIcon className='h-10 w-10 text-emerald-500 mx-auto' />
                            <p className='text-lg text-emerald-600 select-none my-auto'>
                                <b>Drop .fasta file here</b>
                            </p>
                        </div>
                    </div>
                )}
                <ResizableDiv>
                    <HotkeysProvider>
                        <Table2
                            className='sequence-input-table'
                            cellRendererDependencies={[tableRenderInvalidator]}
                            columnWidths={columnWidths}
                            enableColumnHeader={true}
                            enableColumnResizing={false}
                            enableFocusedCell={enableFocusedCell && !isLocked}
                            enableRowHeader={false}
                            focusedCell={isLocked ? undefined : focusedCell}
                            getCellClipboardData={(row: number, col: number) => rows[row]?.[col] ?? ''}
                            key={!containerWidth ? 'loading' : 'loaded'}
                            numRows={rowHeights.length}
                            onFocusedCell={isLocked ? undefined : setFocusedCell}
                            onSelection={
                                isLocked ? undefined : (selectedRegions) => setSelectedRegions(selectedRegions)
                            }
                            ref={tableRef}
                            rowHeights={rowHeights}
                            selectedRegions={isLocked ? [] : selectedRegions}
                        >
                            {columns.map((name, index) => (
                                <Column
                                    key={index}
                                    cellRenderer={cellRenderer}
                                    nameRenderer={() => (
                                        <span
                                            style={{ marginLeft: -2, fontSize: 12 }}
                                            className='text-gray-600 font-bold'
                                        >
                                            {name}
                                        </span>
                                    )}
                                />
                            ))}
                        </Table2>
                    </HotkeysProvider>
                </ResizableDiv>
            </div>
            <div className='flex-none md:w-40 lg:w-36'>
                <input
                    hidden={true}
                    multiple={false}
                    ref={fileInputRef}
                    type='file'
                    onChange={(event) => {
                        onFileSelect(event.target.files);
                        if (fileInputRef?.current) {
                            // Clear the hidden input field so next onChange will work for same file
                            fileInputRef.current.value = '';
                        }
                    }}
                />
                <FetchIdsModal
                    isOpen={fetchIdsModalOpen}
                    onClose={() => setFetchIdsModalOpen(false)}
                    onSave={(sequences, modalIndexToOriginalIndex) => {
                        const rowsToUpdate: Record<number, Record<number, string>> = {};
                        let nextRowIndex = findNextAvailableRowIndex(rows);

                        Object.entries(sequences).forEach(([modalIndex, sequenceData]) => {
                            const originalRowIndex = modalIndexToOriginalIndex[Number(modalIndex)];
                            if (originalRowIndex !== undefined) {
                                // This was prepopulated, update the original row
                                rowsToUpdate[originalRowIndex] = sequenceData as Record<number, string>;
                            } else {
                                // This is new, add after the last element
                                rowsToUpdate[nextRowIndex] = sequenceData as Record<number, string>;
                                nextRowIndex++;
                            }
                        });

                        updateRows(rowsToUpdate, true);
                    }}
                    sequenceRetrievalAppUri={currentUser?.enterprise_settings?.sequence_retrieval_app_uri || ''}
                    existingSequenceRows={rows}
                />
                <div className='justify-between space-y-2'>
                    <UtilityButton
                        disabled={isLocked}
                        onClick={() => {
                            if (fileInputRef?.current) {
                                fileInputRef.current.click();
                            }
                        }}
                    >
                        <DocumentIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                        Upload .fasta
                    </UtilityButton>
                    {currentUser?.enterprise_settings?.sequence_retrieval_app_uri && props.enableFetchIds && (
                        <UtilityButton disabled={isLocked} onClick={() => setFetchIdsModalOpen(true)}>
                            <ArrowLeftEndOnRectangleIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                            Fetch by IDs
                        </UtilityButton>
                    )}
                    <UtilityButton disabled={false} onClick={clearTable}>
                        <TrashIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                        Clear Table
                    </UtilityButton>
                </div>
            </div>
        </div>
    );
}
